import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchTenantUsers,
  addUserToTenant,
  removeUserFromTenant,
} from '../store/slices/adminSlice';
import { listInvites, createInvite, deleteInvite, getTenantIntegrations, redirectToSalesforceLogin, getTenantSalesforceTokens, setDefaultSalesforceToken, revokeUserSalesforceToken } from '../api/auth';
import { triggerFieldSync } from '../api/analytics';
import { generateTraceId, logUserAction } from '../utils/trace';
import DataSourcesTab from '../components/DataSourcesTab';
import TenantsTab from '../components/TenantsTab';

function AdminPage() {
  const dispatch = useDispatch();
  const { user, activeTenant } = useSelector((state) => state.auth);
  const { tenantUsers, isLoading } = useSelector((state) => state.admin);

  const [activeTab, setActiveTab] = useState('users');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [integrations, setIntegrations] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // null, 'syncing', 'success', 'error'
  const [syncError, setSyncError] = useState(null);
  // Per-user token state
  const [userTokens, setUserTokens] = useState(null);
  const [userTokensLoading, setUserTokensLoading] = useState(false);
  const [tokenActionLoading, setTokenActionLoading] = useState(null); // token_id being acted on
  const [showChangeDefaultModal, setShowChangeDefaultModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(null); // token to revoke

  const isAdmin = activeTenant?.role === 'admin' || user?.isGodMode;

  const fetchInvites = async () => {
    setInvitesLoading(true);
    try {
      const data = await listInvites();
      setInvites(data || []);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
    } finally {
      setInvitesLoading(false);
    }
  };

  const fetchIntegrations = async () => {
    setIntegrationsLoading(true);
    try {
      const data = await getTenantIntegrations();
      setIntegrations(data);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const fetchUserTokens = async () => {
    setUserTokensLoading(true);
    try {
      const data = await getTenantSalesforceTokens();
      setUserTokens(data);
    } catch (err) {
      console.error('Failed to fetch user tokens:', err);
    } finally {
      setUserTokensLoading(false);
    }
  };

  const handleSetDefaultToken = async (tokenId) => {
    setTokenActionLoading(tokenId);
    try {
      await setDefaultSalesforceToken(tokenId);
      await fetchUserTokens(); // Refresh the list
    } catch (err) {
      console.error('Failed to set default token:', err);
      alert(err.message || 'Failed to set default token');
    } finally {
      setTokenActionLoading(null);
      setShowChangeDefaultModal(false);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    setTokenActionLoading(tokenId);
    try {
      await revokeUserSalesforceToken(tokenId);
      await fetchUserTokens(); // Refresh the list
    } catch (err) {
      console.error('Failed to revoke token:', err);
      alert(err.message || 'Failed to revoke token');
    } finally {
      setTokenActionLoading(null);
      setShowRevokeModal(null);
    }
  };

  // Helper function to get token status badge
  const getTokenStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Expired
          </span>
        );
      case 'needs_reauth':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Needs Re-auth
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Revoked
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  // Helper to format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  useEffect(() => {
    if (activeTenant) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'page_load', { page: 'admin' });
      dispatch(fetchTenantUsers({ tenantId: activeTenant.tenant_id, traceId }));
      if (isAdmin) {
        fetchInvites();
      }
    }
  }, [dispatch, activeTenant, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
        <p className="text-gray-600">You need admin privileges to access this page.</p>
      </div>
    );
  }

  const handleAddUser = async (e) => {
    e.preventDefault();
    const traceId = generateTraceId();
    logUserAction(traceId, 'add_user', { email: newUserEmail, role: newUserRole });
    await dispatch(
      addUserToTenant({
        tenantId: activeTenant.tenant_id,
        email: newUserEmail,
        role: newUserRole,
        traceId,
      })
    );
    setShowAddUserModal(false);
    setNewUserEmail('');
    setNewUserRole('user');
  };

  const handleRemoveUser = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'remove_user', { userId });
      await dispatch(
        removeUserFromTenant({
          tenantId: activeTenant.tenant_id,
          userId,
          traceId,
        })
      );
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    try {
      await createInvite(inviteEmail, inviteRole);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('user');
      fetchInvites();
    } catch (err) {
      console.error('Failed to create invite:', err);
      alert(err.message || 'Failed to create invite');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (window.confirm('Are you sure you want to cancel this invite?')) {
      try {
        await deleteInvite(inviteId);
        fetchInvites();
      } catch (err) {
        console.error('Failed to delete invite:', err);
      }
    }
  };

  const handleTriggerFieldSync = async () => {
    setSyncStatus('syncing');
    setSyncError(null);
    try {
      const result = await triggerFieldSync();
      console.log('Field sync started:', result);
      setSyncStatus('success');
      // Reset status after 5 seconds
      setTimeout(() => setSyncStatus(null), 5000);
    } catch (err) {
      console.error('Failed to trigger field sync:', err);
      setSyncStatus('error');
      setSyncError(err.message || 'Failed to start field sync');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Admin Panel</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'users'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tenant Users
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'invites'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Pending Invites
          </button>
          <button
            onClick={() => {
              setActiveTab('integrations');
              if (!integrations) fetchIntegrations();
              if (!userTokens) fetchUserTokens();
            }}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'integrations'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Integrations
          </button>
          <button
            onClick={() => setActiveTab('dataSources')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'dataSources'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Data Sources
          </button>
          {user?.isGodMode && (
            <button
              onClick={() => setActiveTab('tenants')}
              className={`pb-4 px-2 font-medium transition ${
                activeTab === 'tenants'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tenants
            </button>
          )}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-bold text-gray-900">
              Users in {activeTenant?.name}
            </h2>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition text-sm font-medium"
            >
              Add User
            </button>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-gray-600">Loading...</div>
          ) : tenantUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-600">No users found.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tenantUsers.map((u) => (
                  <tr key={u.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{u.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 capitalize">
                      {u.role}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleRemoveUser(u.user_id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Invites Tab */}
      {activeTab === 'invites' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-bold text-gray-900">
              Pending Invites
            </h2>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm font-medium"
            >
              Invite User
            </button>
          </div>
          {invitesLoading ? (
            <div className="p-8 text-center text-gray-600">Loading...</div>
          ) : invites.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              No pending invites. Invite users who can then sign in with Google or Salesforce.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Invited
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invites.map((inv) => (
                  <tr key={inv.invite_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{inv.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 capitalize">{inv.role}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(inv.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleDeleteInvite(inv.invite_id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Salesforce Integration Header */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8 text-[#00A1E0]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <div>
                  <h2 className="font-bold text-gray-900">Salesforce Integration</h2>
                  {userTokens && (
                    <p className="text-sm text-gray-500">
                      {userTokens.salesforce_org_name || userTokens.salesforce_org_id || 'Not connected'}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={redirectToSalesforceLogin}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00A1E0] rounded-lg hover:bg-[#0089C1] transition"
              >
                Connect Your Account
              </button>
            </div>
          </div>

          {/* Default Token Card */}
          {userTokensLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">Loading token information...</div>
          ) : userTokens?.tokens?.length > 0 ? (
            <>
              {/* Default Token - Highlighted */}
              {(() => {
                const defaultToken = userTokens.tokens.find(t => t.is_default);
                if (!defaultToken) return null;
                return (
                  <div className="bg-white rounded-lg shadow border-l-4 border-primary-500">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          <span className="font-semibold text-gray-900">Default Token</span>
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">Used for Analytics</span>
                        </div>
                        {getTokenStatusBadge(defaultToken.auth_status)}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="font-medium text-gray-900">
                            {defaultToken.user_name}
                            {defaultToken.user_email === user?.email && (
                              <span className="ml-2 text-xs text-primary-600">(You)</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">{defaultToken.user_email}</p>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <p>Last used: {formatRelativeTime(defaultToken.last_used_at)}</p>
                          <p>Expires: {defaultToken.token_expires_at ? formatRelativeTime(defaultToken.token_expires_at) : 'Unknown'}</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={handleTriggerFieldSync}
                          disabled={syncStatus === 'syncing' || defaultToken.auth_status !== 'active'}
                          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {syncStatus === 'syncing' ? (
                            <>
                              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Syncing...
                            </>
                          ) : syncStatus === 'success' ? (
                            <>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                              Sync Started
                            </>
                          ) : (
                            'Sync Fields'
                          )}
                        </button>
                        {userTokens.tokens.filter(t => t.auth_status === 'active').length > 1 && (
                          <button
                            onClick={() => setShowChangeDefaultModal(true)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                          >
                            Change Default
                          </button>
                        )}
                      </div>
                      {/* Sync Error Message */}
                      {syncStatus === 'error' && syncError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700">{syncError}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* User Tokens Table */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">User Tokens ({userTokens.tokens.length})</h3>
                  <button
                    onClick={fetchUserTokens}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Refresh
                  </button>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Used</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {userTokens.tokens.map((token) => (
                      <tr key={token.token_id} className={`hover:bg-gray-50 ${token.is_default ? 'bg-primary-50' : ''}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {token.user_name}
                                {token.user_email === user?.email && (
                                  <span className="ml-1 text-xs text-primary-600">(You)</span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">{token.user_email}</p>
                            </div>
                            {token.is_default && (
                              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">Default</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getTokenStatusBadge(token.auth_status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatRelativeTime(token.last_used_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {token.user_email === user?.email && (
                              <button
                                onClick={redirectToSalesforceLogin}
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Reconnect
                              </button>
                            )}
                            {!token.is_default && token.auth_status === 'active' && (
                              <button
                                onClick={() => handleSetDefaultToken(token.token_id)}
                                disabled={tokenActionLoading === token.token_id}
                                className="text-sm text-gray-600 hover:text-gray-700 font-medium disabled:opacity-50"
                              >
                                Set Default
                              </button>
                            )}
                            {!token.is_default && (
                              <button
                                onClick={() => setShowRevokeModal(token)}
                                disabled={tokenActionLoading === token.token_id}
                                className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">No Salesforce tokens connected yet.</p>
              <p className="text-sm text-gray-500">
                Connect your Salesforce account to enable field analytics and data synchronization.
                A Salesforce admin account is required for the initial connection.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Data Sources Tab */}
      {activeTab === 'dataSources' && (
        <DataSourcesTab />
      )}

      {/* Tenants Tab (God Mode Only) */}
      {activeTab === 'tenants' && user?.isGodMode && (
        <TenantsTab />
      )}

      {/* Change Default Modal */}
      {showChangeDefaultModal && userTokens?.tokens && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Change Default Token</h3>
            <p className="text-sm text-gray-600 mb-4">
              The default token is used for analytics operations. Select an active token to set as the new default.
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {userTokens.tokens
                .filter(t => t.auth_status === 'active' && !t.is_default)
                .map((token) => (
                  <button
                    key={token.token_id}
                    onClick={() => handleSetDefaultToken(token.token_id)}
                    disabled={tokenActionLoading === token.token_id}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    <p className="font-medium text-gray-900">
                      {token.user_name}
                      {token.user_email === user?.email && (
                        <span className="ml-1 text-xs text-primary-600">(You)</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">{token.user_email}</p>
                  </button>
                ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowChangeDefaultModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Token Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Revoke Token</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to revoke the Salesforce token for <strong>{showRevokeModal.user_name}</strong>?
              They will need to reconnect their Salesforce account to restore access.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRevokeModal(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevokeToken(showRevokeModal.token_id)}
                disabled={tokenActionLoading === showRevokeModal.token_id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                Revoke Token
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Add User</h3>
            <form onSubmit={handleAddUser}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="viewer">Viewer</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddUserModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Invite User</h3>
            <p className="text-sm text-gray-600 mb-4">
              The invited user can sign in with Google or Salesforce and will automatically be added to your tenant.
            </p>
            <form onSubmit={handleCreateInvite}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="user@example.com"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="viewer">Viewer</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
